import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SelectAllIcon from '@mui/icons-material/PlaylistAdd';
import {Box, Button, Icon, IconButton, Tooltip} from '@mui/material';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {focusItems, unfocusItems} from '@explorer/actions/focused-item';
import {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
} from '@explorer/actions/results';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {trackEvent} from '@explorer/analytics';
import {getUniqueId} from '@explorer/util/items';

// NOTE: Now only shows for active results
const BreadcrumbsSection = ({activeResult, ...props}) => {
  const {t} = useTranslation();
  const resetSelectedResult = () => {
    props.setActiveResultId(null);
  };
  const sceneIds = activeResult && activeResult.properties.itemIds;

  const viewFullySelected =
    sceneIds && sceneIds.every(id => props.viewItems.includes(id));

  const orderFullySelected =
    sceneIds && sceneIds.every(id => props.orderItems.includes(id));

  const footprintFullySelected =
    sceneIds &&
    sceneIds.every(id =>
      props.focusedItems.map(item => getUniqueId(item)).includes(id)
    );

  const fullySelected =
    viewFullySelected || orderFullySelected || footprintFullySelected;
  const onSelectAllClick = () => {
    if (fullySelected) {
      trackEvent('Clicked Deselect All on breadcrumbs');
      props.removeResultFromView({resultId: activeResult.id});
      props.removeResultFromOrder({resultId: activeResult.id});
      props.unfocusItems({items: activeResult.properties.items});
    } else {
      trackEvent('Clicked Select All on breadcrumbs');
      props.addResultToView({resultId: activeResult.id});
      props.addResultToOrder({resultId: activeResult.id});
      props.focusItems({items: activeResult.properties.items});
    }
  };

  return (
    <Fragment>
      <Box
        aria-label="results-breadcrumbs"
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mr: 1,
        }}
      >
        {activeResult && (
          <Fragment>
            <Tooltip title={t('resultsList.tooltipBackToResults')}>
              <Button
                size="small"
                color="inherit"
                onClick={resetSelectedResult}
                startIcon={<ArrowBackIcon />}
                data-qe="results-breadcrumb-back"
              >
                {t('common:date.long', {
                  date: activeResult.properties.acquired,
                })}
              </Button>
            </Tooltip>

            <Tooltip
              title={
                fullySelected
                  ? t('resultsList.buttonDeselectAll')
                  : t('resultsList.buttonSelectAll')
              }
            >
              <IconButton
                size="small"
                onClick={onSelectAllClick}
                data-qe={`list-tabs-${
                  fullySelected ? 'deselect' : 'select'
                }-all`}
                color={fullySelected ? 'inherit' : 'primary'}
              >
                <Icon
                  component={fullySelected ? ClearAllIcon : SelectAllIcon}
                />
              </IconButton>
            </Tooltip>
          </Fragment>
        )}
      </Box>
    </Fragment>
  );
};

BreadcrumbsSection.propTypes = {
  activeResult: PropTypes.object,
  addResultToOrder: PropTypes.func,
  addResultToView: PropTypes.func,
  count: PropTypes.number.isRequired,
  orderItems: PropTypes.array,
  focusedItems: PropTypes.array,
  removeResultFromOrder: PropTypes.func,
  removeResultFromView: PropTypes.func,
  setActiveResultId: PropTypes.func,
  viewItems: PropTypes.array,
  focusItems: PropTypes.func,
  unfocusItems: PropTypes.func,
};

const mapStateToProps = state => ({
  activeResult: state.results.resultsById[state.sidebar.activeResultId],
  orderItems: state.results.orderItems,
  viewItems: state.results.viewItems,
  focusedItems: state.focusedItems,
});

const mapDispatchProps = {
  addResultToOrder,
  addResultToView,
  removeResultFromOrder,
  removeResultFromView,
  setActiveResultId,
  unfocusItems,
  focusItems,
};

export default connect(mapStateToProps, mapDispatchProps)(BreadcrumbsSection);
